import { ApiError } from '@dodoc/api-client';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { retryAfterSeconds } from './http';

/** Повторять ли запрос: сеть, 429 (по Retry-After), 502–504. Ошибки 4xx и 500 не повторяются. */
export function shouldRetryRequest(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 0) return true;
  if (error.code === 'RK_UNAVAILABLE') return false;
  return error.status === 429 || error.status === 502 || error.status === 503 || error.status === 504;
}

export function retryDelayMs(attempt: number, error: unknown): number {
  const retryAfter = retryAfterSeconds(error);
  if (retryAfter !== null) return Math.min(retryAfter, 60) * 1000;
  return Math.min(1000 * 2 ** attempt, 8000);
}

export interface QueryClientHooks {
  /** Ошибка мутации без meta.silent — показать уведомление (подключает GlobalErrorToasts). */
  onMutationError?: (error: unknown) => void;
}

/** Метаданные мутации: `meta: { silent: true }` — экран сам показывает ошибку. */
export interface MutationMeta extends Record<string, unknown> {
  silent?: boolean;
}

export function createQueryClient(hooks: QueryClientHooks = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache(),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if ((mutation.meta as MutationMeta | undefined)?.silent) return;
        hooks.onMutationError?.(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => shouldRetryRequest(error) && failureCount < 3,
        retryDelay: retryDelayMs,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
